import React, { useState, useEffect, useRef } from "react";
import Content from "../components/Content";
import Layout from "../containers/LayoutAdministrativo";
import CachedIcon from "@mui/icons-material/Cached";
import CloseIcon from "@mui/icons-material/Close";

import SucursalesService from "../services/SucursalesService";
import AreasService from "../services/AreasService";
import SolicitudesService from "../services/SolicitudesService";
import SolicitudesEstudiosService from "../services/SolicitudesEstudiosService";
import EstudiosService from "../services/EstudiosService";
import EstudiosIntService from "../services/EstudiosIntService";
import ParametrosEstudioService from "../services/ParametrosEstudioService";
import DoctoresService from "../services/DoctoresService";
import socketService from "../services/socketService";


import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Paper,
  Typography,
  OutlinedInput,
  Stack,
  Box,
  Button,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress
} from "@mui/material";
import Swal from "sweetalert2";

const RecepcionMuestras = () => {
  const viewOnly = false;
  const [cargando, setCargando] = useState(false);

  // Filtros
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroSucursal, setFiltroSucursal] = useState("todas");
  const [filtroEstadoRegistro, setFiltroEstadoRegistro] = useState("todas");
  const [filtroArea, setFiltroArea] = useState("todas");

  // Helper para parsear fechas de base de datos en formato dd/mm/yyyy o YYYY-MM-DD
  const parseFecha = (fechaStr) => {
    if (!fechaStr) return null;
    const separator = fechaStr.includes("/") ? "/" : (fechaStr.includes("-") ? "-" : null);
    if (separator) {
      const parts = fechaStr.split(" ")[0].split(separator);
      if (parts.length === 3) {
        if (parts[2].length === 4) {
          // dd/mm/yyyy
          return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
        }
        if (parts[0].length === 4) {
          // yyyy-mm-dd
          return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        }
      }
    }
    const d = new Date(fechaStr);
    if (!isNaN(d.getTime())) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    return null;
  };

  // Helper para parsear la fecha de los inputs de fecha (que siempre vienen en formato YYYY-MM-DD)
  const parseFechaInput = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    }
    return null;
  };

  // Fechas (por defecto la de hoy)
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [fechaInicio, setFechaInicio] = useState(getFechaHoy());
  const [fechaFin, setFechaFin] = useState(getFechaHoy());

  // Catálogos
  const [sucursales, setSucursales] = useState([]);
  const [areas, setAreas] = useState([]);
  const [doctoresMap, setDoctoresMap] = useState({});
  const doctoresMapRef = useRef({});

  // Datos
  const [solicitudesCargadas, setSolicitudesCargadas] = useState([]);

  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = JSON.parse(usuarioString || "{}");

  useEffect(() => {
    document.title = "Recepción de muestras";
    cargarCatalogos();
  }, []);

  useEffect(() => {
    cargarSolicitudesEstudios();

    // WebSockets: Refrescar muestras al recibir evento en tiempo real
    const handleActualizacionRealtime = () => {
      cargarSolicitudesEstudios();
    };

    socketService.on("muestra_actualizada", handleActualizacionRealtime);
    socketService.on("solicitud_actualizada", handleActualizacionRealtime);

    return () => {
      socketService.off("muestra_actualizada", handleActualizacionRealtime);
      socketService.off("solicitud_actualizada", handleActualizacionRealtime);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaInicio, fechaFin]);

  const cargarCatalogos = async () => {
    try {
      const [resSuc, resAreas, resDoc] = await Promise.all([
        SucursalesService.getSucursales(),
        AreasService.getAreas(),
        DoctoresService.getDoctores().catch(() => ({ data: [] }))
      ]);
      setSucursales(resSuc?.data?.sucursales || []);
      setAreas(resAreas?.data?.areas || []);

      const listaDoc = Array.isArray(resDoc?.data?.doctores)
        ? resDoc.data.doctores
        : Array.isArray(resDoc?.data)
        ? resDoc.data
        : [];

      const docMap = {};
      listaDoc.forEach((d) => {
        if (d && (d.idDoctor || d.id)) {
          const idKey = d.idDoctor || d.id;
          const nom = [d.nombre, d.apPaterno, d.apMaterno].filter(Boolean).join(" ").trim();
          docMap[idKey] = nom || d.nombreDoctor || `Dr. #${idKey}`;
        }
      });
      doctoresMapRef.current = docMap;
      setDoctoresMap(docMap);
      return docMap;
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      return {};
    }
  };

  const resolverNombreDoctor = (sol, docMap = {}) => {
    if (!sol) return "A quien corresponda";

    // 1. Array de IDs en idDoctores o idDoctoresN
    const idsArr = Array.isArray(sol.idDoctores)
      ? sol.idDoctores
      : Array.isArray(sol.idDoctoresN)
      ? sol.idDoctoresN
      : [];

    const nombresDesdeIds = idsArr
      .map((id) => docMap[id])
      .filter(Boolean);

    if (nombresDesdeIds.length > 0) {
      return nombresDesdeIds.join(", ");
    }

    // 2. Objetos en arreglo doctores
    if (Array.isArray(sol.doctores) && sol.doctores.length > 0) {
      const nombresObj = sol.doctores
        .map((d) => {
          if (typeof d === "string") return d;
          if (d && typeof d === "object") {
            return (
              [d.nombre, d.apPaterno, d.apMaterno].filter(Boolean).join(" ").trim() ||
              d.nombreDoctor ||
              docMap[d.idDoctor || d.id]
            );
          }
          return null;
        })
        .filter(Boolean);
      if (nombresObj.length > 0) {
        return nombresObj.join(", ");
      }
    }

    // 3. Objeto doctor anidado
    if (sol.doctor && typeof sol.doctor === "object") {
      const nom = [sol.doctor.nombre, sol.doctor.apPaterno, sol.doctor.apMaterno].filter(Boolean).join(" ").trim();
      if (nom) return nom;
    }

    // 4. IDs individuales
    const directIds = [sol.idDoctor, sol.idDoctor1, sol.id_doctor, sol.idDoctor2, sol.idDoctor3, sol.doctorId];
    for (const dId of directIds) {
      if (dId && docMap[dId]) {
        return docMap[dId];
      }
    }

    // 5. Campos de texto directos
    const directName = sol.nombreDoctor || sol.doctor || sol.doctorNombre;
    if (directName && typeof directName === "string" && directName.trim() !== "") {
      return directName.trim();
    }

    return "A quien corresponda";
  };

  const formatearNombreDoctorUI = (nom) => {
    if (!nom) return "A quien corresponda";
    const clean = String(nom).trim();
    if (clean.toLowerCase().includes("a quien corresponda")) {
      return clean;
    }
    if (
      clean.toLowerCase().startsWith("dr.") ||
      clean.toLowerCase().startsWith("dr ") ||
      clean.toLowerCase().startsWith("dra.") ||
      clean.toLowerCase().startsWith("dra ")
    ) {
      return clean;
    }
    return `Dr. ${clean}`;
  };

  const cargarSolicitudesEstudios = async () => {
    try {
      setCargando(true);

      // Asegurar que el catálogo de doctores esté cargado
      let currentDocMap = doctoresMapRef.current;
      if (!currentDocMap || Object.keys(currentDocMap).length === 0) {
        try {
          const resDoc = await DoctoresService.getDoctores().catch(() => ({ data: [] }));
          const listaDoc = Array.isArray(resDoc?.data?.doctores)
            ? resDoc.data.doctores
            : Array.isArray(resDoc?.data)
            ? resDoc.data
            : [];
          currentDocMap = {};
          listaDoc.forEach((d) => {
            if (d && (d.idDoctor || d.id)) {
              const idKey = d.idDoctor || d.id;
              const nom = [d.nombre, d.apPaterno, d.apMaterno].filter(Boolean).join(" ").trim();
              currentDocMap[idKey] = nom || d.nombreDoctor || `Dr. #${idKey}`;
            }
          });
          doctoresMapRef.current = currentDocMap;
          setDoctoresMap(currentDocMap);
        } catch (e) {
          console.error("Error cargando doctores:", e);
        }
      }

      const resSol = await SolicitudesService.getSolicitudes();
      const listaSol = resSol?.data?.solicitudes || [];

      // Filtrar solicitudes por rango de fecha primero para optimizar peticiones
      const solicitudesEnRango = listaSol.filter((sol) => {
        const fSol = parseFecha(sol.fecha);
        const fInicio = parseFechaInput(fechaInicio);
        const fFin = parseFechaInput(fechaFin);
        if (!fSol || !fInicio || !fFin) return false;

        const match = fSol.getTime() >= fInicio.getTime() && fSol.getTime() <= fFin.getTime();
        return match;
      });

      // Cargar los estudios y configuraciones de cada solicitud en paralelo
      const solicitudesProcesadas = await Promise.all(
        solicitudesEnRango.map(async (sol) => {
          try {
            const resEst = await SolicitudesEstudiosService.getEstudiosPorSolicitud(sol.idSolicitud);
            const recepcionesRes = await SolicitudesEstudiosService.getRecepcionMuestrasPorSolicitud(sol.idSolicitud);
            const recepciones = recepcionesRes?.recepciones || [];
            const estudiosList = resEst?.estudios || [];

            const estudiosConDetalles = await Promise.all(
              estudiosList.map(async (est) => {
                try {
                  const estDetalle = await EstudiosService.getEstudioPorId(est.idEstudio);

                  // 1. Verificar si subexámenes están activos
                  let registroExamenSub = false;
                  if (estDetalle) {
                    registroExamenSub =
                      estDetalle.registroExamenSub === true ||
                      estDetalle.registroExamenSub === 1 ||
                      String(estDetalle.registroExamenSub).toLowerCase() === "si";
                  }

                  // 2. Obtener parámetros del estudio principal
                  const params = await ParametrosEstudioService.getParametrosPorEstudio(est.idEstudio) || [];
                  const hasParameters = params.length > 0;

                  // 3. Si tiene subexámenes activos, cargar hijos
                  let subExamenesList = [];
                  if (registroExamenSub) {
                    const resInt = await EstudiosIntService.getEstudiosPorEstudio(est.idEstudio) || [];
                    for (const sub of resInt) {
                      try {
                        const subDetalle = await EstudiosService.getEstudioPorId(sub.idEstudioSec);
                        if (subDetalle) {
                          const matchSub = recepciones.find(
                            (se) => Number(se.idEstudio) === Number(sub.idEstudioSec) && Number(se.idEstudioPadre) === Number(est.idEstudio)
                          );
                          subExamenesList.push({
                            idEstudio: sub.idEstudioSec,
                            idEstudioPadre: est.idEstudio,
                            idSolicitudEstudio: est.idSolicitudEstudio || est.idSolicitudesEstudios || null,
                            idSolicitudesEstudios: est.idSolicitudesEstudios || est.idSolicitudEstudio || null,
                            codigo: subDetalle.codigo || "",
                            nombre: subDetalle.nombre || "",
                            idArea: subDetalle.idArea,
                            registrada: matchSub?.registrada || "P",
                            fechaMuestra: matchSub?.fechaMuestra || null,
                            usuarioMuestra: matchSub?.nombreUsuario || null,
                            idUsuarioMuestra: matchSub?.usuarioMuestra || null,
                            isSubEstudio: true
                          });
                        }
                      } catch (errSub) {
                        console.error("Error al cargar detalle del subestudio:", errSub);
                      }
                    }
                  }

                  const matchMain = recepciones.find(
                    (se) => Number(se.idEstudio) === Number(est.idEstudio) && (!se.idEstudioPadre || Number(se.idEstudioPadre) === 0)
                  );

                  return {
                    ...est,
                    codigo: estDetalle?.codigo || est.codigo || "",
                    nombre: estDetalle?.nombre || est.nombreEstudio || "",
                    idArea: estDetalle?.idArea,
                    registrada: matchMain?.registrada || "P",
                    fechaMuestra: matchMain?.fechaMuestra || null,
                    usuarioMuestra: matchMain?.nombreUsuario || null,
                    idUsuarioMuestra: matchMain?.usuarioMuestra || null,
                    registroExamenSub,
                    hasParameters,
                    subExamenes: subExamenesList,
                    isSubEstudio: false,
                    idEstudioPadre: null
                  };
                } catch (e) {
                  console.error("Error al procesar estudio:", est.idEstudio, e);
                  return {
                    ...est,
                    codigo: est.codigo || "",
                    nombre: est.nombreEstudio || "",
                    registrada: "P",
                    fechaMuestra: null,
                    usuarioMuestra: null,
                    idUsuarioMuestra: null,
                    registroExamenSub: false,
                    hasParameters: true,
                    subExamenes: [],
                    isSubEstudio: false,
                    idEstudioPadre: null
                  };
                }
              })
            );

            const docResuelto = resolverNombreDoctor(sol, currentDocMap);

            return {
              ...sol,
              nombreDoctor: docResuelto,
              estudiosProcesados: estudiosConDetalles
            };
          } catch (errSol) {
            console.error("Error al cargar estudios para solicitud:", sol.idSolicitud, errSol);
            const docResuelto = resolverNombreDoctor(sol, currentDocMap);
            return {
              ...sol,
              nombreDoctor: docResuelto,
              estudiosProcesados: []
            };
          }
        })
      );

      setSolicitudesCargadas(solicitudesProcesadas);
    } catch (err) {
      console.error("Error al cargar solicitudes y estudios:", err);
      mostrarError("Error al cargar solicitudes del servidor.");
    } finally {
      setCargando(false);
    }
  };

  const getFormattedNowLocal = (date = new Date()) => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hours}:${minutes}`;
  };

  const handleRegisterSample = async (sol, study) => {
    const now = new Date();
    const fechaYHora = getFormattedNowLocal(now);

    const payload = {
      idSolicitud: sol.idSolicitud,
      idSolicitudEstudio: study.idSolicitudEstudio || study.idSolicitudesEstudios || null,
      idSolicitudesEstudios: study.idSolicitudesEstudios || study.idSolicitudEstudio || null,
      idEstudio: study.idEstudio,
      idEstudioPadre: study.idEstudioPadre || null,
      registrada: "R",
      fechaMuestra: fechaYHora,
      usuarioMuestra: currentU.id
    };

    try {
      const res = await SolicitudesEstudiosService.updateRegistroMuestra(payload);
      if (res?.data?.estatus === "OK" || res?.data?.status === "OK") {
        // Actualizar el estado localmente sin Swal ni recarga completa
        setSolicitudesCargadas((prevList) => {
          return prevList.map((s) => {
            if (s.idSolicitud !== sol.idSolicitud) return s;
            return {
              ...s,
              estudiosProcesados: s.estudiosProcesados.map((e) => {
                if (e.idEstudio === study.idEstudio && !study.isSubEstudio) {
                  return {
                    ...e,
                    registrada: "R",
                    fechaMuestra: fechaYHora,
                    idUsuarioMuestra: currentU.id,
                    usuarioMuestra: currentU.nombre || currentU.usuario || "Empleado"
                  };
                }
                if (e.registroExamenSub) {
                  return {
                    ...e,
                    subExamenes: e.subExamenes.map((sub) => {
                      if (sub.idEstudio === study.idEstudio && study.isSubEstudio) {
                        return {
                          ...sub,
                          registrada: "R",
                          fechaMuestra: fechaYHora,
                          idUsuarioMuestra: currentU.id,
                          usuarioMuestra: currentU.nombre || currentU.usuario || "Empleado"
                        };
                      }
                      return sub;
                    })
                  };
                }
                return e;
              })
            };
          });
        });
      } else {
        mostrarError(res?.data?.mensaje || "No se pudo registrar la muestra.");
      }
    } catch (error) {
      console.error("Error al registrar muestra:", error);
      mostrarError("Error al comunicarse con el servidor.");
    }
  };

  const mostrarError = (mensaje) => {
    Swal.fire({
      title: "¡Error!",
      text: mensaje,
      icon: "error",
      confirmButtonText: "Aceptar",
    });
  };

  const handleLimpiarFiltros = () => {
    setFiltroTexto("");
    setFiltroSucursal("todas");
    setFiltroEstadoRegistro("todas");
    setFiltroArea("todas");
    setFechaInicio(getFechaHoy());
    setFechaFin(getFechaHoy());
    if (fechaInicio === getFechaHoy() && fechaFin === getFechaHoy()) {
      cargarSolicitudesEstudios();
    }
  };

  const getTimeStampSolicitud = (sol) => {
    const fStr = sol.fecha || sol.fechaSolicitud || sol.fechaCreacion || sol.createdAt || sol.created_at;
    if (fStr) {
      const dateVal = new Date(typeof fStr === "string" ? fStr.replace(/-/g, "/") : fStr);
      if (!Number.isNaN(dateVal.getTime())) return dateVal.getTime();
    }
    return Number(sol.idSolicitud || sol.id || sol.orden || 0);
  };

  // Lógica de filtrado en memoria
  const filtrarYProcesarSolicitudes = () => {
    const query = filtroTexto.toLowerCase().trim();

    return solicitudesCargadas
      .map((sol) => {
        // 1. Filtrar por sucursal
        if (filtroSucursal !== "todas" && String(sol.idSucursal) !== String(filtroSucursal)) {
          return null;
        }

        // 2. Filtrar por texto (código, nombre de paciente o nombre de doctor)
        const codigo = String(sol.codigo || sol.orden || "").toLowerCase();
        const paciente = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.toLowerCase();
        const doctor = String(sol.nombreDoctor || "").toLowerCase();
        if (query && !codigo.includes(query) && !paciente.includes(query) && !doctor.includes(query)) {
          return null;
        }

        // 3. Filtrar estudios y aplicar reglas de visibilidad (con/sin subexámenes y parámetros)
        const estudiosFiltrados = [];

        (sol.estudiosProcesados || []).forEach((est) => {
          const coincideFiltrosPadre =
            (filtroArea === "todas" || String(est.idArea) === String(filtroArea)) &&
            (filtroEstadoRegistro === "todas" || est.registrada === filtroEstadoRegistro);

          if (est.registroExamenSub) {
            // Caso A: Con subexámenes activos.
            // Si el padre tiene parámetros, se muestra (si coincide con los filtros)
            if (est.hasParameters && coincideFiltrosPadre) {
              estudiosFiltrados.push({
                ...est,
                isSubEstudio: false,
                idEstudioPadre: null
              });
            }
            // Los hijos (subexámenes) siempre se muestran individualmente si coinciden con los filtros
            (est.subExamenes || []).forEach((sub) => {
              const coincideFiltrosHijo =
                (filtroArea === "todas" || String(sub.idArea) === String(filtroArea)) &&
                (filtroEstadoRegistro === "todas" || sub.registrada === filtroEstadoRegistro);

              if (coincideFiltrosHijo) {
                estudiosFiltrados.push(sub);
              }
            });
          } else {
            // Caso B: Sin subexámenes activos.
            // Se muestra el padre siempre (aunque no tenga parámetros) si coincide con los filtros
            if (coincideFiltrosPadre) {
              estudiosFiltrados.push({
                ...est,
                isSubEstudio: false,
                idEstudioPadre: null
              });
            }
          }
        });

        // Si la solicitud se quedó sin estudios que mostrar tras los filtros de área/estado, la descartamos
        if (estudiosFiltrados.length === 0) {
          return null;
        }

        return {
          ...sol,
          estudiosVisibles: estudiosFiltrados
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const timeA = getTimeStampSolicitud(a);
        const timeB = getTimeStampSolicitud(b);
        if (timeB !== timeA) return timeB - timeA;
        return Number(b.idSolicitud || b.orden || 0) - Number(a.idSolicitud || a.orden || 0);
      });
  };

  const solicitudesFiltradas = filtrarYProcesarSolicitudes();

  // Renderizar una tabla por sucursal
  const renderTablasPorSucursal = () => {
    // Determinar qué sucursales se deben mostrar en la UI
    const sucursalesAMostrar = filtroSucursal === "todas"
      ? sucursales
      : sucursales.filter(suc => String(suc.idSucursal) === String(filtroSucursal));

    if (sucursalesAMostrar.length === 0) {
      return (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography sx={{ color: "#666" }}>No se encontraron sucursales cargadas.</Typography>
        </Box>
      );
    }

    return sucursalesAMostrar.map((suc) => {
      // Filtrar solicitudes de esta sucursal específica
      const solsDeSucursal = solicitudesFiltradas.filter(
        (sol) => String(sol.idSucursal) === String(suc.idSucursal)
      );

      let consecutiva = 0;

      return (
        <Box key={suc.idSucursal} sx={{ mb: 4, width: "100%" }}>
          {/* Etiqueta de la sucursal idéntica a la captura (estilo pestaña) */}
          <Box
            sx={{
              backgroundColor: "#18234f",
              color: "white",
              display: "inline-block",
              px: "2vw",
              py: "0.4vw",
              borderTopLeftRadius: "6px",
              borderTopRightRadius: "6px",
              fontWeight: "bold",
              fontSize: "0.95rem",
              textTransform: "uppercase"
            }}
          >
            {suc.nombre}
          </Box>
          <Table sx={{ width: "100%", backgroundColor: "white", border: "1px solid #ccc", borderCollapse: "collapse", "& .MuiTableCell-root": { padding: "4px 8px" } }}>
            <TableHead sx={{ backgroundColor: "#18234f" }}>
              <TableRow>
                <TableCell align="center" sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "0.9rem", py: 1, width: "5%" }}>No.</TableCell>
                <TableCell align="center" sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "0.9rem", py: 1, width: "12%" }}>CÓDIGO</TableCell>
                <TableCell align="left" sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "0.9rem", py: 1, width: "30%" }}>PACIENTE</TableCell>
                <TableCell align="left" sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "0.9rem", py: 1, width: "25%" }}>ESTUDIOS</TableCell>
                <TableCell align="center" sx={{ color: "white", fontWeight: "bold", borderRight: "1px solid #ccc", borderBottom: "none", fontSize: "0.9rem", py: 1, width: "13%" }}>REGISTRO</TableCell>
                <TableCell align="center" sx={{ color: "white", fontWeight: "bold", borderBottom: "none", fontSize: "0.9rem", py: 1, width: "15%" }}>EMPLEADO</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {solsDeSucursal.length > 0 ? (
                solsDeSucursal.map((sol) => {
                  consecutiva += 1;
                  const currentConsecutiva = consecutiva;

                  return sol.estudiosVisibles.map((study, idx) => {
                    const esPrimerRenglon = idx === 0;

                    return (
                      <TableRow key={`${sol.idSolicitud}-${study.idEstudio}-${idx}`} hover sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}>
                        {/* Celdas agrupadas: solo se muestran en el primer renglón de la solicitud */}
                        <TableCell align="center" sx={{ borderRight: "1px solid #ccc", borderBottom: "1px solid #ddd", py: 0.8, fontSize: "0.85rem", fontWeight: "bold" }}>
                          {esPrimerRenglon ? currentConsecutiva : ""}
                        </TableCell>
                        <TableCell align="center" sx={{ borderRight: "1px solid #ccc", borderBottom: "1px solid #ddd", py: 0.8, fontSize: "0.85rem", fontWeight: "bold" }}>
                          {esPrimerRenglon ? (sol.codigo || sol.orden) : ""}
                        </TableCell>
                        <TableCell align="left" sx={{ borderRight: "1px solid #ccc", borderBottom: "1px solid #ddd", py: 0.8, fontSize: "0.85rem" }}>
                          {esPrimerRenglon ? (
                            <Box sx={{ whiteSpace: "pre-line" }}>
                              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>
                                {`${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`} ({sol.edad || ""})
                              </Typography>
                              <Typography sx={{ color: "#555", fontStyle: "italic", fontSize: "0.78rem" }}>
                                {formatearNombreDoctorUI(sol.nombreDoctor)}
                              </Typography>
                            </Box>
                          ) : ""}
                        </TableCell>

                        {/* Celdas individuales de cada estudio/subexamen */}
                        <TableCell align="left" sx={{ borderRight: "1px solid #ccc", borderBottom: "1px solid #ddd", py: 0.8, fontSize: "0.85rem", fontWeight: study.isSubEstudio ? "normal" : "bold", pl: study.isSubEstudio ? "2vw" : "8px" }}>
                          {study.isSubEstudio ? `• ${study.nombre}` : study.nombre}
                        </TableCell>
                        <TableCell align="center" sx={{ borderRight: "1px solid #ccc", borderBottom: "1px solid #ddd", py: 0.4 }}>
                          {study.registrada === "R" ? (
                            <Chip
                              label="Registrada"
                              color="success"
                              size="small"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: "#2e7d32",
                                color: "white",
                                borderRadius: "4px",
                                fontSize: "0.75rem"
                              }}
                            />
                          ) : (
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => handleRegisterSample(sol, study)}
                              sx={{
                                minWidth: "2.5rem",
                                width: "2.5rem",
                                height: "1.8rem",
                                padding: 0,
                                borderRadius: "4px",
                                backgroundColor: "#d32f2f",
                                "&:hover": { backgroundColor: "#b71c1c" }
                              }}
                            >
                              <CloseIcon sx={{ fontSize: "1.2rem" }} />
                            </Button>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: "1px solid #ddd", py: 0.4, fontSize: "0.8rem" }}>
                          {study.registrada === "R" ? (
                            <Box sx={{ lineHeight: "1.2" }}>
                              <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>
                                {study.fechaMuestra ? study.fechaMuestra.substring(0, 16) : ""}
                              </Typography>
                              <Typography sx={{ fontSize: "0.76rem", color: "#555" }}>
                                {study.usuarioMuestra || study.nombreUsuarioMuestra || `ID: ${study.idUsuarioMuestra || ""}`}
                              </Typography>
                            </Box>
                          ) : ""}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: "#666", fontSize: "0.85rem" }}>
                    No hay solicitudes registradas
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      );
    });
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            width: "100%",
            paddingTop: "2vh",
            paddingBottom: "2vh",
          }}
        >
          <Stack spacing={2} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper elevation={3} sx={{ p: 2, borderRadius: 4, width: "100%", boxSizing: "border-box" }} className="paperSol">
              {/* Encabezado reducido en altura */}
              <Grid item xs={12}>
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 1 }}>
                  <Typography
                    variant="h9"
                    gutterBottom
                    sx={{
                      background: "linear-gradient(90deg, #006787, #014e65)",
                      color: "white",
                      padding: "0.3vw 1vw",
                      borderRadius: "8px",
                      display: "inline-block",
                      whiteSpace: "pre-line",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: "bold", justifyContent: "center" }}
                    >
                      RECEPCION DE MUESTRAS
                    </Box>
                  </Typography>
                </Box>
              </Grid>

              {/* Sección de Filtros */}
              <Grid container spacing={"0.3vw"}>
                <Grid item xs={12} sx={{ width: "100%" }}>
                  <Table sx={{ width: "100%", backgroundColor: "white", border: "1px solid #ddd", "& .MuiTableCell-root": { padding: "4px 8px" } }}>
                    <TableHead sx={{ backgroundColor: "#18234f" }}>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          align="center"
                          sx={{
                            fontSize: "1.5vw",
                            color: "white",
                            fontWeight: "bold",
                            borderBottom: "none",
                            borderRight: "1px solid #ccc",
                          }}
                        >
                          Filtros
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ "& .MuiTableCell-root": { padding: "4px 6px" } }}>
                      {/* Filtro por Texto */}
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography
                              variant="h9"
                              gutterBottom
                              sx={{
                                fontWeight: "bold",
                                color: "black",
                                padding: "0.5vw 1vw",
                                borderRadius: "8px",
                                display: "inline-block",
                                textAlign: "center",
                              }}
                            >
                              Coloque numero de solicitud o nombre del paciente:
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <TextField
                              disabled={viewOnly}
                              value={filtroTexto}
                              onChange={(e) => setFiltroTexto(e.target.value)}
                              placeholder="Buscar..."
                              sx={{
                                width: "14vw",
                                "& .MuiInputBase-root": {
                                  height: "5vh",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "1vw",
                                },
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Filtro por Sucursal */}
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography
                              variant="h9"
                              gutterBottom
                              sx={{
                                fontWeight: "bold",
                                color: "black",
                                padding: "0.5vw 1vw",
                                borderRadius: "8px",
                                display: "inline-block",
                                textAlign: "center",
                              }}
                            >
                              Buscar sucursal:
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Select
                              input={<OutlinedInput />}
                              disabled={viewOnly}
                              value={filtroSucursal}
                              onChange={(e) => setFiltroSucursal(e.target.value)}
                              sx={{
                                width: "14vw",
                                height: "5vh",
                                minHeight: "0px !important",
                                overflow: "hidden",
                                "&.MuiInputBase-root": {
                                  minHeight: "0px !important",
                                },
                                "& .MuiSelect-select": {
                                  minHeight: "0px !important",
                                  padding: "0 0.5vw !important",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "1vw",
                                  lineHeight: "1",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  top: 0,
                                  "& legend": { display: "none" },
                                },
                                "& .MuiSvgIcon-root": {
                                  display: "inline-block !important",
                                  position: "absolute",
                                  right: "0.5vw",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: "1.2vw !important",
                                  width: "1.2vw !important",
                                  height: "1.2vw !important",
                                  pointerEvents: "none",
                                },
                                minWidth: "0px !important",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <MenuItem value="todas" sx={{ fontSize: "1vw" }}>Todas</MenuItem>
                              {sucursales.map((suc) => (
                                <MenuItem key={suc.idSucursal} value={suc.idSucursal} sx={{ fontSize: "1vw" }}>
                                  {suc.nombre}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Filtro por Estado de Registro */}
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography
                              variant="h9"
                              gutterBottom
                              sx={{
                                fontWeight: "bold",
                                color: "black",
                                padding: "0.5vw 1vw",
                                borderRadius: "8px",
                                display: "inline-block",
                                textAlign: "center",
                              }}
                            >
                              Estado de registro:
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Select
                              input={<OutlinedInput />}
                              disabled={viewOnly}
                              value={filtroEstadoRegistro}
                              onChange={(e) => setFiltroEstadoRegistro(e.target.value)}
                              sx={{
                                width: "14vw",
                                height: "5vh",
                                minHeight: "0px !important",
                                overflow: "hidden",
                                "&.MuiInputBase-root": {
                                  minHeight: "0px !important",
                                },
                                "& .MuiSelect-select": {
                                  minHeight: "0px !important",
                                  padding: "0 0.5vw !important",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "1vw",
                                  lineHeight: "1",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  top: 0,
                                  "& legend": { display: "none" },
                                },
                                "& .MuiSvgIcon-root": {
                                  display: "inline-block !important",
                                  position: "absolute",
                                  right: "0.5vw",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: "1.2vw !important",
                                  width: "1.2vw !important",
                                  height: "1.2vw !important",
                                  pointerEvents: "none",
                                },
                                minWidth: "0px !important",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <MenuItem value="todas" sx={{ fontSize: "1vw" }}>Todas</MenuItem>
                              <MenuItem value="P" sx={{ fontSize: "1vw" }}>Pendiente</MenuItem>
                              <MenuItem value="R" sx={{ fontSize: "1vw" }}>Registrada</MenuItem>
                            </Select>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Filtro por Area */}
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography
                              variant="h9"
                              gutterBottom
                              sx={{
                                fontWeight: "bold",
                                color: "black",
                                padding: "0.5vw 1vw",
                                borderRadius: "8px",
                                display: "inline-block",
                                textAlign: "center",
                              }}
                            >
                              Areas:
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Select
                              input={<OutlinedInput />}
                              disabled={viewOnly}
                              value={filtroArea}
                              onChange={(e) => setFiltroArea(e.target.value)}
                              sx={{
                                width: "14vw",
                                height: "5vh",
                                minHeight: "0px !important",
                                overflow: "hidden",
                                "&.MuiInputBase-root": {
                                  minHeight: "0px !important",
                                },
                                "& .MuiSelect-select": {
                                  minHeight: "0px !important",
                                  padding: "0 0.5vw !important",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "1vw",
                                  lineHeight: "1",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  top: 0,
                                  "& legend": { display: "none" },
                                },
                                "& .MuiSvgIcon-root": {
                                  display: "inline-block !important",
                                  position: "absolute",
                                  right: "0.5vw",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  fontSize: "1.2vw !important",
                                  width: "1.2vw !important",
                                  height: "1.2vw !important",
                                  pointerEvents: "none",
                                },
                                minWidth: "0px !important",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <MenuItem value="todas" sx={{ fontSize: "1vw" }}>Todas</MenuItem>
                              {areas.map((area) => (
                                <MenuItem key={area.idArea} value={area.idArea} sx={{ fontSize: "1vw" }}>
                                  {area.nombre}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Rango de Fechas */}
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Typography
                              variant="h9"
                              gutterBottom
                              sx={{
                                fontWeight: "bold",
                                color: "black",
                                padding: "0.5vw 1vw",
                                borderRadius: "8px",
                                display: "inline-block",
                                textAlign: "center",
                              }}
                            >
                              Seleccione fecha inicial y final:
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", justifyContent: "center", gap: "1vw" }}>
                            <TextField
                              disabled={viewOnly}
                              type="date"
                              value={fechaInicio}
                              onChange={(e) => setFechaInicio(e.target.value)}
                              sx={{
                                width: "14vw",
                                "& .MuiInputBase-root": {
                                  height: "5vh",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "1vw",
                                },
                              }}
                            />
                            <TextField
                              disabled={viewOnly}
                              type="date"
                              value={fechaFin}
                              onChange={(e) => setFechaFin(e.target.value)}
                              sx={{
                                width: "14vw",
                                "& .MuiInputBase-root": {
                                  height: "5vh",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "1vw",
                                },
                              }}
                            />
                            <IconButton onClick={handleLimpiarFiltros} size="small" sx={{ color: "#18234f" }}>
                              <CachedIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </Paper>

            {/* Listado de Tablas de Sucursales */}
            <Paper elevation={3} sx={{ p: 2, borderRadius: 4, width: "100%", boxSizing: "border-box", mt: 2 }} className="paperReportes">
              {cargando ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5 }}>
                  <CircularProgress size="3vw" />
                </Box>
              ) : (
                renderTablasPorSucursal()
              )}
            </Paper>
          </Stack>
        </Box>
      </Content>
    </Layout>
  );
};

export default RecepcionMuestras;
